import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CVIdentityFormProps, CVIdentityData } from './types';
import { validateCVIdentityData } from './utils/validation';
import { useApiRetry } from './hooks/useRetry';
import { useProfileSync } from './hooks/useProfileSync';
import { useAccessibility, useFocusManagement } from './hooks/useAccessibility';
import { CVServiceError } from '../../services/cvService';
import Input from '../UI/Input';
import Button from '../UI/Button';
import ErrorMessage from './components/ErrorMessage';
import { InitialStateUserType } from '../../typings/state';
import {BiSave} from 'react-icons/bi';

/**
 * CV Identity Form component
 * Handles personal information input with validation and pre-population from user profile
 */
const CVIdentityForm: React.FC<CVIdentityFormProps> = ({ 
  initialData, 
  currentPhotoUrl, 
  onSave, 
  isLoading = false 
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<CVServiceError | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  
  // Retry functionality for form submission
  const { executeWithRetry, isRetrying } = useApiRetry();
  
  // Profile synchronization
  const { convertProfileToCVData } = useProfileSync({
    enabled: false // We'll handle sync manually in this component
  });
  
  // Get user profile data from Redux store for pre-population
  const user = useSelector((state: {user: InitialStateUserType}) => state.user);
  const userProfileFromRedux = user.profile;

  // Accessibility hooks
  const {
    announceToScreenReader,
    announceValidationErrors,
    announceSuccess,
    focusFirstError,
    setupFormSection
  } = useAccessibility();

  const { containerRef, setInitialFocus } = useFocusManagement('input:not([disabled])');
  
  // Initialize React Hook Form
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    reset,
    getValues
  } = useForm<CVIdentityData>({
    mode: 'onBlur',
    defaultValues: {
      full_name: '',
      birth_place: '',
      birth_date: '',
      address: '',
      phone_number: '',
      email: '',
      linkedin_url: '',
      portfolio_url: '',
      dream_job: '',
      main_interest: '',
      interest_reason: '',
      profile_summary: '',
      photo: undefined
    }
  });

  // Pre-populate form with user profile data or initial CV data
  useEffect(() => {
    if (initialData) {
      // If we have existing CV data, use it
      reset(initialData);
    } else if (userProfileFromRedux) {
      // Pre-populate from user profile data
      const profileData = convertProfileToCVData(userProfileFromRedux);
      reset(profileData);
    }
  }, [initialData, userProfileFromRedux, setValue, reset, convertProfileToCVData]);

  // Listen for profile updates and sync automatically if no existing data
  useEffect(() => {
    if (!initialData && userProfileFromRedux) {
      const profileData = convertProfileToCVData(userProfileFromRedux);
      // Only update fields that are empty to avoid overwriting user changes
      Object.entries(profileData).forEach(([key, value]) => {
        const currentValue = getValues(key as keyof CVIdentityData);
        if (!currentValue && value) {
          setValue(key as keyof CVIdentityData, value);
        }
      });
    }
  }, [userProfileFromRedux, initialData, convertProfileToCVData, setValue, getValues]);

  // Handle photo file selection
  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setSubmitError(new CVServiceError('File harus berformat JPG, JPEG, atau PNG'));
        return;
      }
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setSubmitError(new CVServiceError('Ukuran file maksimal 2MB'));
        return;
      }
      
      setSelectedPhoto(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Clear any previous errors
      setSubmitError(null);
    }
  };

  // Remove selected photo
  const handleRemovePhoto = () => {
    setSelectedPhoto(null);
    setPhotoPreview(null);
    // Reset file input
    const fileInput = document.getElementById('photo') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Form submission handler with retry logic
  const onSubmit = async (data: CVIdentityData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Validate the form data
      const validationResult = validateCVIdentityData(data);
      
      if (!validationResult.isValid) {
        // Announce validation errors to screen readers
        const errorMessages = Object.values(validationResult.errors).flat();
        announceValidationErrors(errorMessages);
        
        // Focus first error field
        setTimeout(() => {
          if (containerRef.current) {
            focusFirstError(containerRef.current);
          }
        }, 100);
        
        setIsSubmitting(false);
        return;
      }

      // Add photo to data if selected
      const submitData: CVIdentityData = {
        ...data,
        photo: selectedPhoto || undefined
      };

      // Call the onSave callback with retry logic
      await executeWithRetry(() => onSave(submitData));
      
      // Announce success
      announceSuccess('Personal information saved successfully');
      
      // Clear any previous errors on success
      setSubmitError(null);
    } catch (error: any) {
      let errorMessage = 'Gagal menyimpan informasi pribadi. Silakan coba lagi.';
      
      if (error instanceof CVServiceError) {
        setSubmitError(error);
        errorMessage = error.message;
      } else {
        setSubmitError(new CVServiceError(
          errorMessage,
          undefined,
          error
        ));
      }
      
      // Announce error to screen readers
      announceToScreenReader(errorMessage, 'assertive');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Retry form submission
  const retrySubmit = () => {
    if (submitError) {
      handleSubmit(onSubmit)();
    }
  };



  // Set up accessibility features
  useEffect(() => {
    if (containerRef.current) {
      setupFormSection(containerRef.current, 'identity-form');
      setInitialFocus();
    }
  }, [containerRef, setupFormSection, setInitialFocus]);

  return (
    <div className="cv-identity-form" ref={containerRef}>
      <div className="flex flex-wrap">
        <div className="w-full">
          {submitError && (
            <div className="mb-3">
              <ErrorMessage
                title="Gagal Menyimpan"
                message={submitError.message}
                error={submitError.originalError}
                onRetry={submitError.isRetryable ? retrySubmit : undefined}
                onDismiss={() => setSubmitError(null)}
                retryLabel={isRetrying ? "Mencoba lagi..." : "Coba Lagi"}
                showDetails={true}
              />
            </div>
          )}

          <form
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-labelledby="identity-form-heading"
          >
            {/* Photo Upload Section */}
            <div className="flex flex-wrap mb-4">
              <div className="w-full text-center">
                <label htmlFor="photo" className="form-label">
                  Foto Profil
                </label>
                <div className="photo-upload-section">
                  {/* Current photo or preview */}
                  {(photoPreview || currentPhotoUrl) && (
                    <div className="current-photo mb-3 text-center flex justify-center items-center flex-col">
                      <img
                        src={photoPreview || currentPhotoUrl}
                        alt="Foto profil"
                        className="img-thumbnail"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                        }}
                      />
                      {selectedPhoto && (
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn bg-red-500 hover:bg-red-600 text-white"
                            onClick={handleRemovePhoto}
                            disabled={isLoading || isSubmitting}
                          >
                            Hapus Foto
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* File input */}
                  <input
                    type="file"
                    id="photo"
                    className="form-control"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handlePhotoChange}
                    disabled={isLoading || isSubmitting}
                  />
                  <div className="form-text text-xs text-gray-500 mt-1">
                    Format yang didukung: JPG, JPEG, PNG. Maksimal 2MB.
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap">
              {/* Full Name */}
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="full_name" className="form-label">
                  Nama Lengkap{" "}
                  <span className="text-red-600" aria-label="required">
                    *
                  </span>
                </label>
                <Input
                  placeholder="Masukkan nama lengkap Anda"
                  name="full_name"
                  register={register as any}
                  errors={errors as any}
                  rules={{
                    required: "Nama lengkap wajib diisi",
                    minLength: {
                      value: 2,
                      message: "Nama lengkap minimal 2 karakter",
                    },
                  }}
                  block
                  disabled={isLoading || isSubmitting}
                />
                {errors.full_name && (
                  <div role="alert" aria-live="polite" className="sr-only">
                    Kesalahan nama lengkap: {errors.full_name.message}
                  </div>
                )}
              </div>

              {/* Birth Place */}
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="birth_place" className="form-label">
                  Tempat Lahir <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="Masukkan tempat lahir Anda"
                  name="birth_place"
                  register={register as any}
                  errors={errors as any}
                  rules={{
                    required: "Tempat lahir wajib diisi",
                  }}
                  block
                  disabled={isLoading || isSubmitting}
                />
              </div>
            </div>

            <div className="flex flex-wrap">
              {/* Birth Date */}
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="birth_date" className="form-label">
                  Tanggal Lahir <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="YYYY-MM-DD"
                  type="date"
                  name="birth_date"
                  register={register as any}
                  errors={errors as any}
                  rules={{
                    required: "Tanggal lahir wajib diisi",
                  }}
                  block
                  disabled={isLoading || isSubmitting}
                />
              </div>

              {/* Phone Number */}
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="phone_number" className="form-label">
                  Nomor Telepon <span className="text-red-600">*</span>
                </label>
                <Input
                  placeholder="Masukkan nomor telepon Anda"
                  type="tel"
                  name="phone_number"
                  register={register as any}
                  errors={errors as any}
                  rules={{
                    required: "Nomor telepon wajib diisi",
                    pattern: {
                      value: /^[+]?[1-9][\d]{0,15}$/,
                      message: "Masukkan nomor telepon yang valid",
                    },
                  }}
                  block
                  disabled={isLoading || isSubmitting}
                />
              </div>
            </div>

            {/* Address */}
            <div className="w-full mb-3">
              <label htmlFor="address" className="form-label">
                Alamat <span className="text-red-600">*</span>
              </label>
              <textarea
                id="address"
                className="form-control"
                placeholder="Masukkan alamat lengkap Anda"
                rows={3}
                {...register("address", {
                  required: "Alamat wajib diisi",
                })}
                disabled={isLoading || isSubmitting}
              />
              {errors.address && (
                <p className="text-red-600 italic mb-2">
                  {errors.address.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div className="w-full mb-3">
              <label htmlFor="email" className="form-label">
                Alamat Email <span className="text-red-600">*</span>
              </label>
              <Input
                placeholder="Masukkan alamat email Anda"
                type="email"
                name="email"
                register={register as any}
                errors={errors as any}
                rules={{
                  required: "Email wajib diisi",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Masukkan alamat email yang valid",
                  },
                }}
                block
                disabled={isLoading || isSubmitting}
              />
            </div>

            <div className="flex flex-wrap">
              {/* LinkedIn URL */}
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="linkedin_url" className="form-label">
                  URL LinkedIn <span className="text-gray-500">(Opsional)</span>
                </label>
                <Input
                  placeholder="https://linkedin.com/in/profil-anda"
                  type="url"
                  name="linkedin_url"
                  register={register as any}
                  errors={errors as any}
                  rules={{
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "Masukkan URL yang valid",
                    },
                  }}
                  block
                  disabled={isLoading || isSubmitting}
                />
              </div>

              {/* Portfolio URL */}
              <div className="w-full md:w-6/12 mb-3">
                <label htmlFor="portfolio_url" className="form-label">
                  URL Portofolio{" "}
                  <span className="text-gray-500">(Opsional)</span>
                </label>
                <Input
                  placeholder="https://portofolio-anda.com"
                  type="url"
                  name="portfolio_url"
                  register={register as any}
                  errors={errors as any}
                  rules={{
                    pattern: {
                      value: /^https?:\/\/.+/,
                      message: "Masukkan URL yang valid",
                    },
                  }}
                  block
                  disabled={isLoading || isSubmitting}
                />
              </div>
            </div>

            {/* Dream Job */}
            <div className="w-full mb-3">
              <label htmlFor="dream_job" className="form-label">
                Pekerjaan Impian <span className="text-red-600">*</span>
              </label>
              <Input
                placeholder="Apa pekerjaan impian Anda?"
                name="dream_job"
                register={register as any}
                errors={errors as any}
                rules={{
                  required: "Pekerjaan impian wajib diisi",
                }}
                block
                disabled={isLoading || isSubmitting}
              />
            </div>

            {/* Main Interest */}
            <div className="w-full mb-3">
              <label htmlFor="main_interest" className="form-label">
                Minat Utama <span className="text-red-600">*</span>
              </label>
              <Input
                placeholder="Apa minat profesional utama Anda?"
                name="main_interest"
                register={register as any}
                errors={errors as any}
                rules={{
                  required: "Minat utama wajib diisi",
                }}
                block
                disabled={isLoading || isSubmitting}
              />
            </div>

            {/* Interest Reason */}
            <div className="w-full mb-3">
              <label htmlFor="interest_reason" className="form-label">
                Alasan Minat <span className="text-red-600">*</span>
              </label>
              <textarea
                id="interest_reason"
                className="form-control"
                placeholder="Jelaskan mengapa Anda tertarik pada bidang ini"
                rows={3}
                {...register("interest_reason", {
                  required: "Alasan minat wajib diisi",
                })}
                disabled={isLoading || isSubmitting}
              />
              {errors.interest_reason && (
                <p className="text-red-600 italic mb-2">
                  {errors.interest_reason.message}
                </p>
              )}
            </div>

            {/* Profile Summary */}
            <div className="w-full mb-4">
              <label htmlFor="profile_summary" className="form-label">
                Ringkasan Profil <span className="text-red-600">*</span>
              </label>
              <textarea
                id="profile_summary"
                className="form-control"
                placeholder="Tulis ringkasan singkat tentang diri Anda dan latar belakang profesional"
                rows={4}
                {...register("profile_summary", {
                  required: "Ringkasan profil wajib diisi",
                  minLength: {
                    value: 50,
                    message: "Ringkasan profil minimal 50 karakter",
                  },
                })}
                disabled={isLoading || isSubmitting}
              />
              {errors.profile_summary && (
                <p className="text-red-600 italic mb-2">
                  {errors.profile_summary.message}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
              <Button
                type="submit"
                primary
                disabled={isLoading || isSubmitting || isRetrying || !isDirty}
                className="w-full flex items-center gap-2 gradient-primary"
              >
                <BiSave />{" "}
                <span>
                  {isSubmitting || isRetrying ? "Menyimpan..." : "Simpan"}
                </span>
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CVIdentityForm;